import {
  prisma,
  type Prisma,
  EventTranslations,
  ResourceType,
  ActionType,
  PaymentType,
  UserRoleAssignmentEntityType,
  OrderStatus,
} from "client";
import { setNewEventWithDefaultTemplates } from "services/defaultTemplate";
import { EVENT_FILTERS } from "@sajilni/config";
import { invalidateEventPageCacheForEvent } from "./eventPageCache";

export { invalidateEventPageCacheForEvent } from "./eventPageCache";

export const CURRENCY_LOCKED_AFTER_PAID_ORDER_ERROR =
  "Currency cannot be changed after a completed paid order exists";

/**
 * True when the event has at least one non-deleted completed order with total > 0.
 * Uses Order @@index([eventId, status, total]); deletedAt is filtered after the index scan.
 */
export const eventHasCompletedPaidOrder = async (
  eventId: string
): Promise<boolean> => {
  const order = await prisma.order.findFirst({
    where: {
      eventId,
      status: OrderStatus.COMPLETED,
      total: { gt: 0 },
      OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
    },
    select: { id: true },
  });
  return Boolean(order);
};

const resolveCurrencyUpdateValue = (
  currency: Prisma.EventUpdateInput["currency"]
): string | undefined => {
  if (currency == null) return undefined;
  if (typeof currency === "string") return currency;
  if (typeof currency === "object" && "set" in currency && currency.set != null) {
    return String(currency.set);
  }
  return undefined;
};
export const getEvents = async (
  userId: string,
  whiteLabelId?: string,
  where?: Prisma.EventWhereInput,
  payload?: Omit<Prisma.EventFindManyArgs, "where" | "include">,
  lang?: string
) => {
  try {
    const events = await prisma.event.findMany({
      where: {
        whiteLabelId,
        ...where,
      },
      include: {
        branding: true,
        venues: {
          where: {
            deletedAt: null,
          },

          include: {
            translations: {
              include: {
                language: true,
              },
            },
          },
        },
        translations: lang
          ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
          : true,
      },
      ...payload,
    });

    return events.map((event: any) => ({
      ...event,
      ...(event?.translations[0] || {}),
      ...(event?.branding || {}),
      id: event.id,
      venues: event?.venues?.map((venue: any) => ({
        ...venue,
        ...(venue?.translations?.[0] || {}),
        id: venue.id,
      })),
    }));
  } catch (error: any) {
    return error;
  }
};

export const getEventOrganizations = async (slug: string) => {
  const event = await prisma.event.findUnique({
    where: { slug },
    select: {
      organizationIds: true,
    },
  });

  return event;
};
export const getEventOrganizationsById = async (id: string) => {
  const event = await prisma.event.findUnique({
    where: { id },
    select: {
      organizationIds: true,
    },
  });

  return event;
};

export const getEventsByWhiteLabelIdLocalized = async (
  userId: string,
  whiteLabelId: string,
  lang: string = "en",
  searchStr?: string,
  filter?: EVENT_FILTERS
) => {
  let where: Prisma.EventWhereInput = {};
  switch (filter) {
    case EVENT_FILTERS.ACTIVE:
      where = {
        published: true,
      };
      break;
    case EVENT_FILTERS.INACTIVE:
      where = {
        published: false,
      };
      break;
    case EVENT_FILTERS.ARCHIVED:
      where = {
        deletedAt: { not: null },
      };
      break;
    case EVENT_FILTERS.UPCOMING:
      where = {
        OR: [
          {
            publishedAt: {
              gte: new Date(),
            },
          },
          {
            startDate: {
              gte: new Date(),
            },
          },
        ],
      };
      break;
  }

  // Escape special regex characters in searchStr if it exists
  const escapedSearchStr = searchStr
    ? searchStr.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    : undefined;

  const events = await prisma.event.findMany({
    where: {
      whiteLabelId: whiteLabelId,
      ...(escapedSearchStr && {
        translations: {
          some: {
            name: {
              contains: escapedSearchStr,
              mode: "insensitive",
            },
          },
        },
      }),
      ...where,
    },
    include: {
      venues: {
        where: {
          deletedAt: null,
        },
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      },
      translations: {
        include: {
          language: true,
        },
      },
      whiteLabel: {
        select: {
          subdomain: true,
        },
      },
    },
    orderBy: {
      createdAt: "desc",
    },
  });
  return events.map((event) => ({
    ...event,
    ...(event.translations?.find((t) => t.language.code === lang) ||
      event.translations?.[0]),
    id: event.id,
    venues: event?.venues?.map((venue) => ({
      ...venue,
      ...(venue?.translations?.[0] || {}),
      id: venue.id,
    })),
  }));
};

export const getEventsByOrganizationIdLocalized = async (
  userId: string,
  organizationId: string,
  lang: string = "en",
  searchStr?: string,
  filter?: EVENT_FILTERS
) => {
  let where: Prisma.EventWhereInput = {};
  switch (filter) {
    case EVENT_FILTERS.ACTIVE:
      where = {
        published: true,
      };
      break;
    case EVENT_FILTERS.INACTIVE:
      where = {
        published: false,
      };
      break;
    // case EVENT_FILTERS.DRAFT:
    //   where = {
    //     published: false,
    //   };
    //   break;
    case EVENT_FILTERS.ARCHIVED:
      where = {
        deletedAt: { not: null },
      };
      break;
    case EVENT_FILTERS.UPCOMING:
      where = {
        publishedAt: {
          gte: new Date(),
        },
      };
      break;
  }

  const events = await prisma.event.findMany({
    where: {
      organizationIds: {
        has: organizationId,
      },
      translations: {
        some: {
          language: {
            code: lang,
          },
          name: {
            contains: searchStr,
            mode: "insensitive",
          },
        },
      },
      ...where,
    },
    include: {
      translations: {
        where: {
          language: {
            code: lang,
          },
        },
      },
      whiteLabel: {
        select: {
          subdomain: true,
        },
      },
    },
    orderBy: {
      createdAt: "desc",
    },
  });

  return events.map((event) => ({
    ...event,
    ...event.translations[0],
    id: event.id,
  }));
};
export const getEventsWithWhiteLabel = async (
  userId: string,
  whiteLabelId?: string,
  payload?: Pick<Prisma.EventFindManyArgs, "orderBy" | "take">,
  where?: Prisma.EventWhereInput
) => {
  try {
    const events = await prisma.event.findMany({
      where: {
        whiteLabelId,
        ...where,
      },
      include: {
        venues: {
          where: {
            deletedAt: null,
          },
          include: {
            translations: {
              include: {
                language: true,
              },
            },
          },
        },
        whiteLabel: true,
      },

      ...payload,
    });

    return events.map((event) => ({
      ...event,
      venues: event?.venues?.map((venue) => ({
        ...venue,
        ...(venue?.translations?.[0] || {}),
        id: venue.id,
      })),
    }));
  } catch (error: any) {
    return error;
  }
};

export const getEvent = async (
  // userId: string,
  id: string,
  lang?: string,
  includes?: Prisma.EventInclude
) => {
  const event = await prisma.event.findUnique({
    where: { id },
    include: {
      ...(includes || {}),
      taxRegion: true,
      category: {
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      },
      venues: {
        where: {
          deletedAt: null,
        },
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      },
      translations: {
        include: {
          language: true,
        },
      },
    },
  });

  if (lang && event?.translations?.[0]) {
    return {
      ...event,
      ...event.translations.find((t) => t.language.code === lang),
      category: event?.category?.translations.find(
        (t) => t.language.code === lang
      ),
      venues: event?.venues?.map((venue) => ({
        ...venue,
        ...(venue?.translations?.[0] || {}),
        id: venue.id,
      })),
      id: event.id,
    };
  }
  return event;
};

export const getEventWithEvenGroup = async (id: string, lang?: string) => {
  try {
    const event = await prisma.event.findUnique({
      where: { id },
      include: {
        venues: {
          where: {
            deletedAt: null,
          },
          include: {
            translations: {
              include: {
                language: true,
              },
            },
          },
        },
        eventGroups: true,
        translations: lang
          ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
          : true,
      },
    });

    if (lang && event?.translations?.[0]) {
      return {
        ...event,
        name: event?.translations[0].name,
        description: event?.translations[0].description,
        venues: event?.venues?.map((venue) => ({
          ...venue,
          ...(venue?.translations?.[0] || {}),
          id: venue.id,
        })),
      };
    }

    return event;
  } catch (error: any) {
    return error;
  }
};
export const getEventWithPages = async (userId: string, id: string) => {
  try {
    const event = await prisma.event.findUnique({
      where: { id },
      include: {
        // pages: true,
      },
    });
    return { ...event, pages: [] };
  } catch (error: any) {
    return error;
  }
};

export const getEventWithWLSubdomain = async (id: string, lang?: string) => {
  const event = await prisma.event.findUnique({
    where: {
      id,
    },
    include: {
      whiteLabel: {
        select: {
          subdomain: true,
          id: true,
          branding: {
            select: {
              logo: true,
            },
          },
        },
      },
      branding: {
        select: {
          logo: true,
        },
      },
      translations: {
        where: {
          language: {
            code: lang,
          },
        },
      },
    },
  });

  if (lang && event?.translations?.[0]) {
    return {
      ...event,
      ...event.translations[0],
      ...event.branding,
      whiteLabel: {
        ...event.whiteLabel,
        ...event.whiteLabel?.branding,
        id: event.whiteLabel?.id,
      },
      id: event.id,
    };
  }

  return event;
};

/**
 * Full event loader by slug (tickets, venues, orgs, floorPlans, taxRegion, etc.).
 * Prefer slim helpers below unless the caller needs nested relations.
 */
export const getEventBySlug = async (slug: string, lang?: string) => {
  return await prisma.event.findUnique({
    where: {
      slug: slug,
    },
    include: {
      taxRegion: true,
      translations: {
        where: {
          language: {
            code: lang,
          },
        },
      },
      whiteLabel: true,
      organizations: {
        include: {
          translations: lang
            ? {
              where: {
                language: {
                  code: lang,
                },
              },
            }
            : true,
        },
      },
      tickets: true,
      venues: {
        where: {
          deletedAt: null,
        },
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      },
      floorPlans: {
        where: {
          deletedAt: null,
        },
      },
    },
  });
};

/** Resolve slug → `{ id }` only. Pass `select` to add extra scalar/relation fields. */
export const getEventIdBySlug = async (
  slug: string,
  select?: Prisma.EventSelect
) => {
  return prisma.event.findUnique({
    where: { slug },
    select: {
      id: true,
      ...select,
    },
  });
};

/** Sessions routes: `{ id, timezone }`. */
export const getEventBySlugForSessions = async (slug: string) => {
  return prisma.event.findUnique({
    where: { slug },
    select: {
      id: true,
      timezone: true,
    },
  });
};

/**
 * Lightweight event for agenda / calendar UIs.
 * Includes lang-filtered translations when `lang` is provided.
 */
export const getEventBySlugMinimal = async (slug: string, lang?: string) => {
  return prisma.event.findUnique({
    where: { slug },
    select: {
      id: true,
      slug: true,
      timezone: true,
      startDate: true,
      endDate: true,
      languagesIds: true,
      translations: lang
        ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
        : true,
    },
  });
};

/**
 * Meetings / notifications: id, timezone, translations, whiteLabel subdomain.
 */
export const getEventBySlugForMeetings = async (
  slug: string,
  lang?: string
) => {
  return prisma.event.findUnique({
    where: { slug },
    select: {
      id: true,
      slug: true,
      timezone: true,
      translations: lang
        ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
        : true,
      whiteLabel: {
        select: {
          subdomain: true,
        },
      },
    },
  });
};

/** @deprecated Prefer getEventIdBySlug for id-only, or slim helpers for extra fields. */
export const getEventId = async (slug: string, select?: Prisma.EventSelect) => {
  const event = await prisma.event.findUnique({
    where: {
      slug: slug,
    },
    select: {
      id: true,
      timezone: true,
      translations: true,
      ...select,
    },
  });
  return event;
};
export const getEventById = async (id: string, select?: Prisma.EventSelect) => {
  const event = await prisma.event.findUnique({
    where: {
      id: id,
    },
    select: {
      id: true,
      ...select,
    },
  });
  return event;
};
export const generateEventNumber = async (whiteLabelId: string) => {
  try {
    const latestOrder = await prisma.event.findFirst({
      where: {
        whiteLabelId,
      },
      orderBy: { number: "desc" },
    });

    const lastNumber = latestOrder ? latestOrder.number : 0;
    return lastNumber + 1;
  } catch (error: any) {
    console.error(error);
    return error;
  }
};
// CREATE
export const createEvent = async (
  userId: string,
  eventPayload: Prisma.EventCreateInput & { venue: Prisma.VenueCreateInput },
  Translations: EventTranslations[],
  whiteLabelId: string,
  organizationId?: string
) => {
  try {
    let { venue, ...rest } = eventPayload;
    rest = {
      ...rest,
      number: await generateEventNumber(whiteLabelId),
    };
    const event = await prisma.event.create({
      data: {
        ...rest,
        ...(organizationId && {
          organizations: {
            connect: { id: organizationId },
          },
        }),

        translations: {
          createMany: {
            data: Translations,
          },
        },
        defaultCheckInRules: {
          create: {
            name: "Default Check-in Rule",
            isDefault: true,
            // 1 successful check-in per ticket reference number
            numberOfScans: 1,
            // Checkout without check-in is not allowed (default setting)
            isCheckoutRequired: true,
            // No zones assigned by default
            zonesIds: [],
          },
        },
      },
    });
    const defaultTemplates = await setNewEventWithDefaultTemplates(
      userId,
      event.id
    );
    return event;
  } catch (error: any) {
    return error;
  }
};

// UPDATE
export const updateEvent = async (
  userId: string,
  id: string,
  eventPayload: Prisma.EventUpdateInput,
  Translations?: EventTranslations[]
) => {
  const translations = Translations
    ? {
      translations: {
        updateMany: Translations.map((t) => ({
          where: {
            languageId: t.languageId,
          },
          data: {
            name: t.name,
            description: t.description,
            about: t.about,
            timeLimitDescription: t.timeLimitDescription,
            timeOutMessage: t.timeOutMessage,
            orderCompleteMessage: t.orderCompleteMessage,
            thankyouPageTitle: t.thankyouPageTitle,
            thankyouPageMessage: t.thankyouPageMessage,
            thankYouPageDescription: t.thankYouPageDescription,
            pageTitle: t.pageTitle,
            buyButtonText: t.buyButtonText,
            thankYouPageButtonLabel: t.thankYouPageButtonLabel,
            thankYouPageTabTitle: t.thankYouPageTabTitle,
            ticketListTitle: t.ticketListTitle,
            ticketListDescription: t.ticketListDescription,
            checkoutPageTitle: t.checkoutPageTitle,
            checkoutDescription: t.checkoutDescription,
            checkoutButtonText: t.checkoutButtonText,
            floorPlanButtonText: t.floorPlanButtonText,
            eventPageDescriptionMetadata: t.eventPageDescriptionMetadata,
            eventPageHTMLCustomContent: t.eventPageHTMLCustomContent,
          },
        })),
      },
    }
    : {};
  try {
    const nextCurrency = resolveCurrencyUpdateValue(eventPayload.currency);
    if (nextCurrency !== undefined) {
      const currentEvent = await prisma.event.findUnique({
        where: { id },
        select: { currency: true },
      });
      if (currentEvent && nextCurrency !== currentEvent.currency) {
        const isCurrencyLocked = await eventHasCompletedPaidOrder(id);
        if (isCurrencyLocked) {
          throw new Error(CURRENCY_LOCKED_AFTER_PAID_ORDER_ERROR);
        }
      }
    }

    const event = await prisma.event.update({
      where: {
        id,
      },
      include: {
        translations: true,
      },
      data: {
        ...eventPayload,
        ...translations,
      },
    });
    await invalidateEventPageCacheForEvent(id, event.slug);
    return event;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

// DELETE
export const deleteEvent = async (userId: string, id: string) => {
  try {
    const event = await prisma.event.delete({
      where: {
        id,
      },
    });
    return event;
  } catch (error: any) {
    return error;
  }
};

// READ
export const getEventsByConditions = async (
  where?: Prisma.EventWhereInput,
  payload?: Omit<Prisma.EventFindManyArgs, "where" | "include">
) => {
  const events = await prisma.event.findMany({
    where: {
      ...where,
    },
    ...payload,
  });

  return events;
};

// READ ORGANIZATION
export const getEventsByOrganization = async (organizationId?: string) => {
  const events = await prisma.event.findMany({
    where: {
      ...(organizationId && {
        organizationIds: {
          hasSome: [organizationId],
        },
      }),
    },
    select: {
      translations: true,
      id: true,
    },
  });
  return events;
};

// get event days

export const getEventDays = async (eventId: string) => {
  // Fetch the event by ID
  const event = await prisma.event.findUnique({
    where: { slug: eventId },
    select: {
      startDate: true,
      endDate: true,
    },
  });

  // Handle case where event is not found
  if (!event) {
    throw new Error(`Event with ID ${eventId} not found.`);
  }

  const { startDate, endDate } = event;

  // Validate that event has valid dates
  if (!startDate || !endDate) {
    console.error(`Event with ID ${eventId} has incomplete date information.`);
    return [];
  }

  // Generate the days range
  const days = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    days.push(new Date(currentDate)); // Add current date to the array
    currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
  }

  return days;
};

export const getEventsByWhiteLabelId = async (
  whiteLabelId: string,
  lang: string = "en",
  searchStr?: string,
  filter?: EVENT_FILTERS
) => {
  try {
    let where: Prisma.EventWhereInput = {};
    switch (filter) {
      case EVENT_FILTERS.ACTIVE:
        where = {
          published: true,
        };
        break;
      case EVENT_FILTERS.INACTIVE:
        where = {
          published: false,
        };
        break;
      case EVENT_FILTERS.ARCHIVED:
        where = {
          deletedAt: { not: null },
        };
        break;
      case EVENT_FILTERS.UPCOMING:
        where = {
          publishedAt: {
            gte: new Date(),
          },
        };
        break;
    }

    // Escape special regex characters in searchStr if it exists
    const escapedSearchStr = searchStr
      ? searchStr.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      : undefined;

    const events = await prisma.event.findMany({
      where: {
        whiteLabelId: whiteLabelId,
        ...(escapedSearchStr && {
          translations: {
            some: {
              name: {
                contains: escapedSearchStr,
                mode: "insensitive",
              },
            },
          },
        }),
        ...where,
      },
      include: {
        translations: {
          where: {
            language: {
              code: lang,
            },
          },
          include: {
            language: true,
          },
        },
      },
    });
    return events;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getEventNetworkingSetting = async (eventId: string) => {
  try {
    const eventNetworkingSettings = await prisma.event.findUnique({
      where: {
        id: eventId,
      },
      select: {
        networkingSettings: true,
      },
    });
    return eventNetworkingSettings?.networkingSettings;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Get events for a user based on their roleV2 permissions
 * - SUPER_ADMIN: all events under white label
 * - WHITE_LABEL_SUPER_ADMIN/WHITE_LABEL_ADMIN: all events under white label
 * - Organization roles: events for assigned organizations (filtered by white label)
 * - Profile roles: events from user profiles with roleV2Id (if no user-level role)
 * - Fallback: return empty array if no permissions
 */
export const getEventsByUserPermissions = async (
  userId: string,
  whiteLabelId: string,
  lang: string = "en",
  organizationId?: string | undefined
) => {
  try {
    // Get user with roleV2 and organizationIds
    const user = await prisma.user.findUnique({
      where: { id: userId },
      select: {
        roleV2Id: true,
        organizationIds: true,
      },
    });

    if (!user) {
      return [];
    }
    if (organizationId) {
      return await getEventsByOrganizationIdLocalized(
        userId,
        organizationId,
        lang
      );
    }
    // Check if user has roleV2 at user level
    if (user.roleV2Id) {
      const userRole = await prisma.roleV2.findUnique({
        where: { id: user.roleV2Id },
        select: { name: true },
      });

      const roleName = userRole?.name;

      // SUPER_ADMIN, WHITE_LABEL_SUPER_ADMIN, WHITE_LABEL_ADMIN: return all events under white label
      if (
        roleName === "SUPER_ADMIN" ||
        roleName === "WHITE_LABEL_SUPER_ADMIN" ||
        roleName === "WHITE_LABEL_ADMIN"
      ) {
        return await getEventsByWhiteLabelIdLocalized(
          userId,
          whiteLabelId,
          lang
        );
      }

      // Organization roles: events for assigned organizations
      if (
        roleName === "ORGANIZATION_SUPER_ADMIN" ||
        roleName === "ORGANIZATION_OWNER" ||
        roleName === "ORGANIZATION_MANAGER" ||
        roleName === "ORGANIZATION_ADMIN" ||
        roleName === "ORGANIZATION_MEMBER" ||
        roleName === "DOOR_AGENT"
      ) {
        // Check if user has organizationIds
        if (user.organizationIds && user.organizationIds.length > 0) {
          // Get events for all organizations the user has access to
          const allOrgEvents = await Promise.all(
            user.organizationIds.map((orgId) =>
              getEventsByOrganizationIdLocalized(userId, orgId, lang)
            )
          );

          // Flatten and deduplicate events, filter by white label
          const eventMap = new Map<string, any>();
          allOrgEvents.flat().forEach((event: any) => {
            // Only include events that belong to this white label
            // whiteLabelId should be included in the event object from getEventsByOrganizationIdLocalized
            if (event?.whiteLabelId === whiteLabelId && event?.id) {
              eventMap.set(event.id, event);
            }
          });
          return Array.from(eventMap.values());
        }
        // If no organizations assigned, check for profile-level access
      }
    }

    // If user has no roleV2Id at user level OR has organization role but no organizations,
    // check for profile-level access (roles assigned at profile level)
    // Get all profiles where user has a roleV2Id at profile level for events in this white label
    const profiles = await prisma.profile.findMany({
      where: {
        userId: userId,
        roleV2Id: { not: null }, // User has a role at profile level
        event: {
          whiteLabelId: whiteLabelId,
        },
      },
      select: {
        eventId: true,
        roleV2Id: true,
      },
    });
    if (profiles.length > 0) {
      const eventIds = profiles
        .map((p) => p.eventId)
        .filter((id): id is string => Boolean(id));
      // Get full event data with translations
      const events = await prisma.event.findMany({
        where: {
          id: { in: eventIds },
          // whiteLabelId: whiteLabelId,
        },
        include: {
          venues: {
            where: {
              deletedAt: null,
            },
            include: {
              translations: {
                include: {
                  language: true,
                },
              },
            },
          },
          translations: {
            include: {
              language: true,
            },
          },
          whiteLabel: {
            select: {
              subdomain: true,
            },
          },
        },
      });
      return events;
    }

    // No roleV2 at user level and no profile-level roles: return empty array
    return [];
  } catch (error) {
    console.error("Error fetching events by user permissions:", error);
    throw error;
  }
};
